/**
 * 지우려는 편성을 **어디서 쓰고 있는가** — 서버가 삭제를 막으며 돌려준 목록(`details.references`)을 읽는다.
 *
 * 예전에는 "아직 참조 중인 플레이리스트는 삭제할 수 없습니다" 만 떠서, 스케줄·캠페인에서만 쓰이면 어디를
 * 봐야 할지 알 수 없었다(2026-10-07 사용자 지적). 이제 쓰는 곳을 종류별로 이름·링크와 함께 보여 준다.
 */
import { ApiCallError } from './api/client.js';
import { josa } from './josa.js';

export type ReferenceKind = 'display' | 'direct' | 'group' | 'schedule' | 'campaign' | 'emergency';

export interface Reference {
  kind: ReferenceKind;
  id: string;
  name: string;
  note?: string;
}

export interface BlockedBy {
  message: string;
  references: Reference[];
  counts: Record<ReferenceKind, number>;
}

interface KindInfo {
  /** 묶음 제목. */
  label: string;
  unit: string;
  /** 누르면 갈 곳. 콘솔에서 고칠 수 없는 것(그룹 — 연동 시스템이 정한다)은 없다. */
  href: ((ref: Reference) => string) | null;
}

export const REFERENCE_KINDS: Record<ReferenceKind, KindInfo> = {
  display: {
    label: '화면 배정',
    unit: '대',
    href: (ref) => `/displays/${ref.id}`,
  },
  direct: {
    label: '바로 보내기',
    unit: '대',
    href: (ref) => `/displays/${ref.id}`,
  },
  group: {
    label: '그룹 배정',
    unit: '개',
    href: null,
  },
  schedule: {
    label: '스케줄',
    unit: '개',
    href: () => '/schedules',
  },
  campaign: {
    label: '캠페인',
    unit: '개',
    href: () => '/campaigns',
  },
  emergency: {
    label: '긴급 송출',
    unit: '건',
    href: () => '/campaigns',
  },
};

export const REFERENCE_ORDER: ReferenceKind[] = ['display', 'direct', 'group', 'schedule', 'campaign', 'emergency'];

/** 삭제가 쓰는 곳 때문에 막혔으면 그 목록, 아니면 null(다른 오류는 평소처럼 문장으로 보인다). */
export function blockedBy(cause: unknown): BlockedBy | null {
  if (!(cause instanceof ApiCallError) || cause.status !== 409) return null;
  const details = cause.details as { references?: unknown; counts?: unknown } | undefined;
  if (!Array.isArray(details?.references) || details.references.length === 0) return null;
  const references = (details.references as Reference[]).filter((ref) => ref && ref.kind in REFERENCE_KINDS);
  const counts = { display: 0, direct: 0, group: 0, schedule: 0, campaign: 0, emergency: 0 };
  for (const kind of REFERENCE_ORDER) {
    const given = (details.counts as Partial<Record<ReferenceKind, number>> | undefined)?.[kind];
    counts[kind] = typeof given === 'number' ? given : references.filter((ref) => ref.kind === kind).length;
  }
  return { message: cause.message, references, counts };
}

/** 종류별 묶음 — 그릴 순서대로, 빈 종류는 뺀다. 목록에 다 싣지 못한 수는 `more` 다. */
export function groupReferences(
  blocked: BlockedBy,
): Array<{ kind: ReferenceKind; items: Reference[]; more: number }> {
  return REFERENCE_ORDER.filter((kind) => blocked.counts[kind] > 0).map((kind) => {
    const items = blocked.references.filter((ref) => ref.kind === kind);
    return { kind, items, more: Math.max(0, blocked.counts[kind] - items.length) };
  });
}

/** 지우려는 것의 종류 — 그래도 지울 때 쓰던 곳에 일어나는 일이 다르다. */
export type DeleteSubject = 'playlist' | 'schedule';

/** 나가고 있는 긴급이 쓰면 그래도 지울 수 없다 — 서버도 막는다(긴급은 긴급 패널에서 해제한다). */
export function forceBlocked(blocked: BlockedBy): boolean {
  return blocked.counts.emergency > 0;
}

function joinCounts(parts: Array<[number, string]>): string {
  return parts
    .filter(([n]) => n > 0)
    .map(([n, label]) => `${label} ${n}${label === '화면' ? '대' : label === '바로 보내기' ? '건' : '개'}`)
    .join(' · ');
}

/** 그래도 지우면 쓰던 곳에 일어나는 일 한 줄 — "무엇이" 와 "어떻게" 를 나눠 두 열로 보인다. */
export interface Consequence {
  target: string;
  effect: string;
  /** 되돌릴 수 없이 사라지는 것(캠페인 삭제) — 눈에 띄게 한다. */
  danger?: boolean;
}

/** 둘 다 "로" 가 그대로 붙는다(받침 없음 · ㄹ 받침) — 다른 종류를 더하면 그 문장의 조사를 다시 본다. */
export const DELETE_NOUN: Record<DeleteSubject, string> = { playlist: '플레이리스트', schedule: '스케줄' };

/**
 * 그래도 지우면 무슨 일이 일어나는가 — 누르기 전에 한 줄씩 보여 준다. 서버가 하는 일(`releasePlaylist`·
 * `releaseSchedule`)과 같은 말이어야 한다.
 *
 * 문장이 아니라 "대상 | 결과" 로 적는다(2026-10-07 사용자 지적 — 모달이 난잡하다). 문장으로 늘어놓으면 대상
 * 이름이 조사에 묻혀 훑어지지 않는다.
 */
export function releaseConsequences(
  subject: DeleteSubject,
  mode: 'replace' | 'detach',
  counts: BlockedBy['counts'],
  replacementName: string | null,
): Consequence[] {
  const lines: Consequence[] = [];
  const assigned = joinCounts([
    [counts.display, '화면'],
    [counts.group, '그룹'],
  ]);
  if (mode === 'replace') {
    const moved = subject === 'playlist'
      ? joinCounts([
          [counts.display, '화면'],
          [counts.group, '그룹'],
          [counts.schedule, '스케줄'],
          [counts.campaign, '캠페인'],
        ])
      : assigned;
    const effect = replacementName
      ? `「${replacementName}」${josa(replacementName, '으로/로')} 교체`
      : `선택한 ${DELETE_NOUN[subject]}로 교체`;
    if (moved) lines.push({ target: moved, effect });
  } else {
    if (assigned) lines.push({ target: assigned, effect: '배정 해제 — 그룹 편성 또는 대기 화면으로 전환' });
    if (subject === 'playlist' && counts.schedule > 0)
      lines.push({ target: `스케줄 ${counts.schedule}개`, effect: '평소 편성에서 제외, 해당 규칙 삭제' });
    if (subject === 'playlist' && counts.campaign > 0)
      lines.push({ target: `캠페인 ${counts.campaign}개`, effect: '삭제', danger: true });
  }
  if (subject === 'playlist' && counts.direct > 0)
    lines.push({ target: `바로 보내기 ${counts.direct}건`, effect: '종료 — 평소 편성으로 복귀' });
  return lines;
}
